import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate } from 'react-router-dom';
import { Award, Gift, Star, TrendingUp, Clock, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useAuthStore } from '@/store';
import axios from 'axios';

import { API } from '@/config/api';

export default function LoyaltyPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuthStore();
  const [status, setStatus] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useLatestCallback(async () => {
    try {
      const [statusRes, rewardsRes] = await Promise.all([
        axios.get(`${API}/loyalty/status`, { headers }),
        axios.get(`${API}/loyalty/rewards`, { headers })
      ]);
      setStatus(statusRes.data);
      setRewards(rewardsRes.data);
    } catch (error) {
      toast.error('Failed to load loyalty data');
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth?redirect=/loyalty');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, fetchData]);

  const redeemReward = async (reward) => {
    setRedeeming(reward.id);
    try {
      // customer-service POST /api/loyalty/redeem/reward: the customer is the
      // token's; /api/loyalty/redeem is the POS (staff) door.
      const res = await axios.post(`${API}/loyalty/redeem/reward`,
        { reward_id: reward.id },
        { headers }
      );
      toast.success(`Redeemed! Your promo code: ${res.data.promo_code}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem');
    } finally {
      setRedeeming(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  const progressToNext = status?.next_tier 
    ? ((status.points - (status.tier.min_points || 0)) / (status.next_tier.points_needed + status.points - (status.tier.min_points || 0))) * 100
    : 100;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Loyalty Rewards</h1>
        <p className="text-muted-foreground">Earn points with every order and unlock exclusive rewards</p>
      </div>

      {/* Points Card */}
      <Card className="mb-8 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm text-muted-foreground">Your Points</p>
              <p className="text-4xl font-bold">{status?.points?.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <span className="text-3xl">{status?.tier?.icon}</span>
                <div>
                  <p className="font-semibold text-lg">{status?.tier?.name}</p>
                  <p className="text-xs text-muted-foreground">{status?.tier?.multiplier}x points</p>
                </div>
              </div>
            </div>
          </div>

          {status?.next_tier && (
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>{status.tier.name}</span>
                <span>{status.next_tier.icon} {status.next_tier.name}</span>
              </div>
              <Progress value={progressToNext} className="h-2" />
              <p className="text-sm text-muted-foreground mt-2 text-center">
                {status.next_tier.points_needed.toLocaleString()} more points to {status.next_tier.name}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How to Earn */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" /> How to Earn Points
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Star className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Place Orders</p>
                <p className="text-sm text-muted-foreground">Earn 1 point per $1 spent</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Gift className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Refer Friends</p>
                <p className="text-sm text-muted-foreground">Get 100 bonus points</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Award className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Level Up</p>
                <p className="text-sm text-muted-foreground">Higher tiers = more points</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Rewards */}
      <h2 className="text-xl font-semibold mb-4">Redeem Rewards</h2>
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {rewards.map((reward) => (
          <Card key={reward.id} className={!reward.can_redeem ? 'opacity-60' : ''}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{reward.name}</h3>
                  <p className="text-sm text-muted-foreground">{reward.description}</p>
                </div>
                <Badge variant={reward.can_redeem ? 'default' : 'secondary'}>
                  {reward.points_cost} pts
                </Badge>
              </div>
              <Button 
                className="w-full mt-4" 
                disabled={!reward.can_redeem || redeeming === reward.id}
                onClick={() => redeemReward(reward)}
              >
                {redeeming === reward.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : reward.can_redeem ? (
                  'Redeem'
                ) : (
                  `Need ${reward.points_cost - status.points} more pts`
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Points History */}
      <h2 className="text-xl font-semibold mb-4">Points History</h2>
      <Card>
        <CardContent className="p-0">
          {status?.history?.length === 0 ? (
            <p className="p-4 text-center text-muted-foreground">No transactions yet. Place an order to earn points!</p>
          ) : (
            <div className="divide-y">
              {status?.history?.map((tx) => (
                <div key={tx.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                      tx.type === 'earned' ? 'bg-green-100 text-green-600' :
                      tx.type === 'redeemed' ? 'bg-red-100 text-red-600' :
                      'bg-muted'
                    }`}>
                      {tx.type === 'earned' ? '+' : '-'}
                    </div>
                    <div>
                      <p className="font-medium">{tx.description}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(tx.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`font-semibold ${tx.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.points > 0 ? '+' : ''}{tx.points}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}